<script setup lang="ts">
import type { UserFavorites } from '~/composables/useFavorites'
import type { CardProps } from '~/utils/card'
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons'

export interface ContentItem {
  id: string
  nodeType: string
  title?: string | null
  coverImage?: string
  date?: string | null
  authors?: string
  issue?: string
  editionNumber?: string
  publisherId?: string | null
  remoteId?: string
  subTitle?: string | null
  bodytext?: string
  journalTitle?: string
}

const props = defineProps<Omit<CardProps, 'type'> & {
  item: ContentItem
}>()

const doc = computed(() => props.item)

const { isFavorite, toggleFavorite } = useFavorites()

const hasLicense = useHasLicense()

type UserFavoritesKey = keyof UserFavorites

const favoriteCategory = computed<UserFavoritesKey>(() => {
  const nodeType = doc.value.nodeType

  if (nodeType === 'legal_commentary') {
    return 'law'
  }
  if (nodeType === 'book') {
    return 'literature'
  }
  return 'journals'
})

const coverImage = computed(() => {
  const nodeType = doc.value.nodeType

  if (nodeType === 'academic_article') {
    return undefined
  }

  if (nodeType === 'journal_article' || nodeType === 'journal_editorial') {
    return props.item.coverImage ?? usePublicAssetUrl('images/cover.svg')
  }

  if (nodeType === 'legal_commentary') {
    return props.item.coverImage ?? usePublicAssetUrl('images/commentary.svg')
  }

  return props.item.coverImage
})

const displayFavoriteButton = computed(() => {
  return doc.value.id && favoriteCategory.value !== undefined
})
const { coverFallbackImage, isCoverImageFailed, shouldRenderCoverImage, handleCoverImageError } = useCoverImageFallback(() => coverImage.value)

watch(() => doc.value.id, () => {
  isCoverImageFailed.value = false
})

const shouldRenderFallbackImage = computed(() => {
  const nodeType = doc.value.nodeType
  return !shouldRenderCoverImage.value
    ? (nodeType === 'book' || nodeType === 'journal_article' || nodeType === 'journal_editorial' || nodeType === 'legal_commentary')
    : false
})

const cardType = computed(() => {
  const nodeType = doc.value.nodeType
  if (nodeType === 'academic_article') {
    return 'article'
  }
  if (nodeType === 'journal_article' || nodeType === 'journal_editorial') {
    return 'book'
  }
  return 'book'
})

const bookMeta = computed(() => {
  if (doc.value.nodeType !== 'book') {
    return undefined
  }
  const edition = doc.value.editionNumber ? `${doc.value.editionNumber}. utgave` : undefined
  const year = doc.value.date ? formatDate(new Date(doc.value.date), { year: 'numeric' }) : undefined
  if (!edition && !year) {
    return undefined
  }
  return [edition, year].filter(Boolean).join(' · ')
})

const displaySubTitle = computed(() => {
  const nodeType = doc.value.nodeType
  return doc.value.subTitle && (nodeType === 'legal_commentary' || nodeType === 'journal_article' || nodeType === 'journal_editorial')
})

const url = useContentUrl(doc)
</script>

<template>
  <Card :type="cardType" v-bind="props" size="medium" class="relative shrink-0 h-[419px]" :to="url" :class="url ? 'cursor-pointer' : 'cursor-default'">
    <template #header>
      <FavoriteButton v-if="displayFavoriteButton" type="book" :selected="isFavorite(favoriteCategory, doc.id)" :aria-label="isFavorite(favoriteCategory, doc.id) ? `Fjern ${doc.title} fra favoritter` : `Legg til ${doc.title} i favoritter`" @click.prevent="toggleFavorite(favoriteCategory, doc.id)" />
      <Icon v-else-if="!hasLicense && (doc.nodeType === 'journal_article' || doc.nodeType === 'journal_editorial')" :icon="faLockKeyhole" class="absolute top-8 right-8 text-carbon-500" />
    </template>
    <img
      v-if="shouldRenderCoverImage"
      :src="coverImage"
      alt=""
      class="object-cover"
      :class="cardVariants.image({ type: 'book', size, presentation })"
      @error="handleCoverImageError"
    >
    <img
      v-else-if="shouldRenderFallbackImage"
      :src="coverFallbackImage"
      alt="Omslag"
      class="object-cover"
      :class="cardVariants.image({ type: 'book', size, presentation })"
    >
    <div class="flex flex-col gap-m w-full">
      <h5 class="text-center line-clamp-2" v-text="doc.title" />
      <div class="flex flex-col items-center gap-3xs">
        <p v-if="displaySubTitle" class="text-caption text-secondary text-center line-clamp-2" v-text="doc.subTitle" />
        <span v-if="bookMeta || doc.date" class="text-caption text-secondary text-center">
          <template v-if="bookMeta">{{ bookMeta }}</template>
          <template v-else-if="doc.date && (doc.nodeType !== 'journal_article' && doc.nodeType !== 'journal_editorial')">{{ formatDate(new Date(doc.date), { year: 'numeric' }) }}</template>
        </span>
        <div v-if="doc.authors?.length" class="flex flex-wrap text-caption text-secondary gap-3xs justify-center">
          <span class="text-center after:content-[','] last:after:content-['']">
            {{ doc.authors }}
          </span>
        </div>
      </div>
    </div>
  </Card>
</template>
